import React, { useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Avatar,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
} from "@mui/material";
import {
    ExpandMore,
    LocationOn,
    Star,
    AccessTime,
    Person,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import AppointmentBookingModal from "./AppointmentBookingModal";
import "./DoctorSlotsDisplay.scss";

const inferImageMimeFromBase64 = (base64 = "") => {
    const trimmed = (base64 || "").trim();
    if (trimmed.startsWith("/9j")) return "image/jpeg";
    if (trimmed.startsWith("iVBOR")) return "image/png";
    if (trimmed.startsWith("R0lGOD")) return "image/gif";
    return "image/png";
};

const DoctorSlotsDisplay = ({ doctors, job_id, patient, specialization, orgCode, appMode }) => {
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);

    // Safety check
    if (!doctors || !Array.isArray(doctors) || doctors.length === 0) {
        return null;
    }

    const handleSlotSelect = (doctor, slot, timeRange, shiftNumber) => {
        if (slot.status === "occupied") {
            toast.warning("This slot is already booked");
            return;
        }
        setSelectedDoctor(doctor);
        setSelectedSlot({ ...slot, timeRange, shiftNumber });
        setBookingModalOpen(true);
    };

    const handleBookingClose = () => {
        setBookingModalOpen(false);
        setSelectedDoctor(null);
        setSelectedSlot(null);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getDoctorImageSrc = (doctor) => {
        if (doctor.photo) {
            const base64 = String(doctor.photo || "").trim();
            const mime = inferImageMimeFromBase64(base64);
            return `data:${mime};base64,${base64}`;
        }
        return null;
    };

    const renderSlots = (slots, timeRange, doctor, shiftNumber) => {
        if (!slots || !Array.isArray(slots) || slots.length === 0) {
            return null;
        }
        return (
            <Box className="slots-grid">
                {slots.map((slot, index) => {
                    const isFree = slot.status === "free";
                    const isOccupied = slot.status === "occupied";

                    return (
                        <button
                            key={index}
                            disabled={isOccupied}
                            onClick={() => handleSlotSelect(doctor, slot, timeRange, shiftNumber)}
                            className="slot-button"
                            style={{
                                padding: "8px 16px",
                                fontSize: "0.875rem",
                                borderRadius: "4px",
                                border: isFree ? "1px solid #1976d2" : "1px solid #ccc",
                                backgroundColor: isOccupied ? "#f5f5f5" : isFree ? "#fff" : "#e0e0e0",
                                color: isOccupied ? "#999" : isFree ? "#1976d2" : "#666",
                                cursor: isOccupied ? "not-allowed" : "pointer",
                                transition: "all 0.2s",
                                minWidth: "120px",
                                textTransform: "none",
                            }}
                            onMouseEnter={(e) => {
                                if (!isOccupied) {
                                    e.target.style.backgroundColor = isFree ? "#e3f2fd" : "#d5d5d5";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isOccupied) {
                                    e.target.style.backgroundColor = isFree ? "#fff" : "#e0e0e0";
                                }
                            }}
                        >
                            {slot.slot_start_time} - {slot.slot_end_time}
                        </button>
                    );
                })}
            </Box>
        );
    };

    const renderDoctorSlots = (doctor) => {
        const slots = doctor?.next_available_slots;
        if (!slots) return null;

        return (
            <Box className="doctor-slots-container">
                {doctor.next_available_date && (
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                        Available on {formatDate(doctor.next_available_date)}
                    </Typography>
                )}

                {slots.first_shift && Array.isArray(slots.first_shift) && slots.first_shift.length > 0 && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="medium">
                                Morning Shift
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {slots.first_shift.map((shift, idx) => (
                                <Box key={idx} sx={{ mb: 2 }}>
                                    {shift.time_range && (
                                        <Typography variant="caption" color="text.secondary">
                                            {shift.time_range}
                                        </Typography>
                                    )}
                                    {shift.slots && renderSlots(shift.slots, shift.time_range, doctor, "first")}
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}

                {slots.second_shift && Array.isArray(slots.second_shift) && slots.second_shift.length > 0 && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="medium">
                                Afternoon Shift
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {slots.second_shift.map((shift, idx) => (
                                <Box key={idx} sx={{ mb: 2 }}>
                                    {shift.time_range && (
                                        <Typography variant="caption" color="text.secondary">
                                            {shift.time_range}
                                        </Typography>
                                    )}
                                    {shift.slots && renderSlots(shift.slots, shift.time_range, doctor, "second")}
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}

                {slots.third_shift && Array.isArray(slots.third_shift) && slots.third_shift.length > 0 && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="medium">
                                Evening Shift
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {slots.third_shift.map((shift, idx) => (
                                <Box key={idx} sx={{ mb: 2 }}>
                                    {shift.time_range && (
                                        <Typography variant="caption" color="text.secondary">
                                            {shift.time_range}
                                        </Typography>
                                    )}
                                    {shift.slots && renderSlots(shift.slots, shift.time_range, doctor, "third")}
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        );
    };

    return (
        <Box className="doctors-slots-display">
            {doctors.map((doctor, index) => {
                const doctorImageSrc = getDoctorImageSrc(doctor);
                return (
                    <Card
                        key={doctor.doctor_id || index}
                        className="doctor-card"
                        sx={{ mb: 3 }}
                    >
                        <CardContent>
                            <Box
                                className="doctor-header"
                                onClick={() => {
                                    setSelectedDoctor(doctor);
                                }}
                                sx={{ cursor: "pointer" }}
                            >
                                <Box className="doctor-info">
                                    <Avatar
                                        src={doctorImageSrc}
                                        alt={doctor.name}
                                        sx={{ width: 64, height: 64, mr: 2 }}
                                    >
                                        <Person />
                                    </Avatar>
                                    <Box className="doctor-details">
                                        <Typography variant="h6" fontWeight="bold">
                                            {doctor.salutation ? `${doctor.salutation} ` : ""}
                                            {doctor.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {doctor.specialization}
                                        </Typography>
                                        <Box className="doctor-meta" sx={{ mt: 1 }}>
                                            {doctor.rating > 0 && (
                                                <Chip
                                                    icon={<Star />}
                                                    label={typeof doctor.rating === 'number' ? doctor.rating.toFixed(1) : doctor.rating}
                                                    size="small"
                                                    sx={{
                                                        mr: 1,
                                                        backgroundColor: '#ff9800',
                                                        color: '#fff',
                                                        '& .MuiChip-icon': {
                                                            color: '#fff'
                                                        }
                                                    }}
                                                />
                                            )}
                                            {doctor.location && (
                                                <Chip
                                                    icon={<LocationOn />}
                                                    label={`${doctor.location?.city || ""} ${doctor.location?.state ? `, ${doctor.location.state}` : ""
                                                        }`.trim()}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mr: 1 }}
                                                />
                                            )}
                                            {doctor.consultation_fees !== undefined && (
                                                <Chip
                                                    icon={<AccessTime />}
                                                    label={`₹${doctor.consultation_fees}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {renderDoctorSlots(doctor)}
                        </CardContent>
                    </Card>
                );
            })}

            {selectedDoctor && selectedSlot && (
                <AppointmentBookingModal
                    open={bookingModalOpen}
                    onClose={handleBookingClose}
                    doctor={selectedDoctor}
                    slot={selectedSlot}
                    patient={patient}
                    job_id={job_id}
                    orgCode={orgCode}
                    appMode={appMode}
                />
            )}
        </Box>
    );
};

export default DoctorSlotsDisplay;

